import {
  AmazonDeliveryCard,
  AnalyticsTrackingEvent,
  BuyBoxDialog,
  isProductEligibleForCheckout,
  isProductOutOfStock,
  isShopifyProduct,
  ProductRewardSearchResult,
} from '@brandclub/common-ui';
import { useTheme } from '@mui/material';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { Suspense, useEffect, useRef, useState } from 'react';

import { useDomainConfig } from '@/api/cache/appConfigQueryOptions';
import { useCurrentBrandclubProduct } from '@/api/cache/dtcProductQueryOptions';
import { useTrackActions } from '../../hooks/useTracking';
import { useBCRouting } from '../../providers/BCRoutingContextProvider';
import { HOST_DOMAIN } from '../../utils/domain';
import { useAddToBagWrite } from './useAddToBagWrite';
import { AddToBagButton } from './AddToBagButtonBase';
import { CatalogV2InpageAddToBagContent, getCatalogV2PriorityProduct } from './CatalogV2InpageAddToBagContent';
import { resolveCatalogV2AddToBagCta } from './resolveCatalogV2AddToBagCta';
import { getProductsToListForBuyBox, StoreLocator } from './inpageAddToBagHelpers';
import { ScrollSliderContainer } from './InpageAddToBagStyles';
import { SubscribeAndSaveOptions, useSubscribeAndSaveChoice } from './SubscribeAndSaveOptions';
import { useAutoUpdateCurrentDtcProducts } from './useAutoUpdateCurrentDtcProducts';

let bodyFont: string | undefined;
/** The store's first body font, so Amazon's card matches the page. Read once, when the first card renders. */
const pageFontFamily = () => {
  bodyFont ??= getComputedStyle(document.body).fontFamily.split(',')[0]?.replace(/["']/g, '').trim() ?? '';
  return bodyFont || undefined;
};

const CatalogV2InpageAddToBagButton = ({ mobile }: { mobile?: boolean }) => {
  const theme = useTheme();
  const [isScrollSliderOn, setIsScrollSliderOn] = useState(false);
  const [isBuyBoxOpen] = useState(false);
  const inPageButtonRef = useRef<HTMLDivElement | null>(null);
  const addToBag = useAddToBagWrite();

  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: window.location.hostname,
      bcSplit: (window as any).bcSplit,
    },
  });

  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  useEffect(() => {
    const handleScroll = () => {
      if (inPageButtonRef.current) {
        const rect = inPageButtonRef.current.getBoundingClientRect();
        const distance = mobile
          ? theme.settings?.featureFlags?.scrollSliderDisplayDistanceMobile
          : theme.settings?.featureFlags?.scrollSliderDisplayDistance;
        const threshold = Number(distance) || 0;
        setIsScrollSliderOn(rect.top < -threshold);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // trigger once on mount to set initial state correctly
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const enableScrollSliderDisplay = mobile
    ? !!theme.settings?.featureFlags?.scrollSliderDisplayMobile
    : !!theme.settings?.featureFlags?.scrollSliderDisplay;
  const enableLocationMap = mobile
    ? !!theme.settings?.featureFlags?.storeLocatorMapMobile
    : !!theme.settings?.featureFlags?.storeLocatorMap;

  const bcRouting = useBCRouting();
  const [trackCheckoutBarAction] = useTrackActions();

  const { data: domainConfig } = useDomainConfig();
  const { data: currentBrandclubProduct } = useCurrentBrandclubProduct();
  const priorityProduct = getCatalogV2PriorityProduct(currentBrandclubProduct);

  useAutoUpdateCurrentDtcProducts(currentBrandclubProduct);

  useEffect(() => {
    if (!priorityProduct) {
      return;
    }
    console.log('[BC Checkout] Selected in-page priority product', {
      flow: 'catalog-v2',
      retailerDisplayName: priorityProduct.retailerDisplayName,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      stacklineSku: priorityProduct.stacklineSku,
    });
  }, [
    priorityProduct?.retailerDisplayName,
    priorityProduct?.retailerId,
    priorityProduct?.retailerSku,
    priorityProduct?.stacklineSku,
  ]);

  const isEligibleForCheckout =
    !!priorityProduct &&
    isProductEligibleForCheckout(priorityProduct as ProductRewardSearchResult, domainConfig?.checkoutAllowedOfferType);
  const hasMarketplaceOffers = !!currentBrandclubProduct?.matchedProducts?.some(
    (product) => !isShopifyProduct(product.retailerId),
  );
  const isOutOfStock =
    !!priorityProduct &&
    isProductOutOfStock(priorityProduct as ProductRewardSearchResult, domainConfig?.checkoutAllowedOfferType);
  const addToBagCta = resolveCatalogV2AddToBagCta({
    isEligibleForCheckout,
    whereToBuyFallback: priorityProduct?.whereToBuyFallback === true,
    hasMarketplaceOffers,
    isLinkedToInventory: priorityProduct?.isLinkedToInventory,
    isOutOfStock,
  });
  const canViewAlternativeOffers = addToBagCta === 'view';
  const hideAddToBagCta = addToBagCta === 'hide';

  const subscribeAndSave = useSubscribeAndSaveChoice(priorityProduct?.stacklineSku);
  const buttonSubscription =
    subscribeAndSave.offer && subscribeAndSave.frequency
      ? { priceCents: subscribeAndSave.offer.subscriberUnitPriceCents, frequency: subscribeAndSave.frequency }
      : undefined;

  const addPriorityProduct = () => {
    if (priorityProduct) void addToBag(priorityProduct as ProductRewardSearchResult, 1, subscribeAndSave.frequency);
  };

  // Used by BuyBoxDialog and LocationMap - BuyBoxDialog handles disable check via its disabledCheckout prop
  const handleBuyClick = (product: ProductRewardSearchResult) => {
    if (!product) {
      return;
    }

    const isCurrentProductEligibleForCheckout = isProductEligibleForCheckout(
      product,
      domainConfig?.checkoutAllowedOfferType,
    );

    if (isCurrentProductEligibleForCheckout && !disableCheckoutByDomain) {
      void addToBag(product, 1);
    } else {
      trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
        stacklineSku: product.stacklineSku,
        retailerId: product.retailerId,
        retailerSku: product.retailerSku,
        retailPrice: product.retailPrice,
        source: 'brandclub_dtc_checkout_bar',
      });

      bcRouting.navigate(product.retailerProductPageUrl, '_blank');
    }
  };

  const handleNonEligibleClick = () => {
    if (canViewAlternativeOffers && currentBrandclubProduct) {
      const productsToList = getProductsToListForBuyBox(currentBrandclubProduct);
      BuyBoxDialog.show({
        products: productsToList,
        checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
        disabledCheckout: disableCheckoutByDomain,
        onClick: handleBuyClick,
        onProductPageClick: (product) => {
          trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
            stacklineSku: product.stacklineSku,
            retailerId: product.retailerId,
            retailerSku: product.retailerSku,
            retailPrice: product.retailPrice,
            organicType: 'openRetailerProductPage',
            source: 'brandclub_dtc_catalog_v2_view_button',
          });
          window.open(product.retailerProductPageUrl, '_blank');
        },
      });
      return;
    }
    if (!priorityProduct) return;
    trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
      stacklineSku: priorityProduct.stacklineSku,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      retailPrice: priorityProduct.retailPrice,
      source: 'brandclub_dtc_checkout_bar',
    });
    trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
      stacklineSku: priorityProduct.stacklineSku,
      retailerId: priorityProduct.retailerId,
      retailerSku: priorityProduct.retailerSku,
      retailPrice: priorityProduct.retailPrice,
      organicType: 'openRetailerProductPage',
      source: 'brandclub_dtc_pdp_view_button',
    });
    bcRouting.navigate(priorityProduct.retailerProductPageUrl, '_blank');
  };

  const primeTargetId = isEligibleForCheckout ? priorityProduct?.primeDeliveryTargetId : undefined;
  const amazonSku = priorityProduct?.amazonSku;

  const renderAddToBagButton = ({ hideRewards }: { hideRewards: boolean }) =>
    hideAddToBagCta ? null : (
      <>
        {isEligibleForCheckout && !disableCheckoutByDomain && priorityProduct ? (
          <SubscribeAndSaveOptions choice={subscribeAndSave} oneTimePrice={priorityProduct.retailPrice} />
        ) : null}
        <AddToBagButton
          ref={inPageButtonRef}
          mobile={mobile}
          onAddToCart={addPriorityProduct}
          onViewRetailer={handleNonEligibleClick}
          currentBrandclubProduct={priorityProduct as ProductRewardSearchResult}
          originalProduct={currentBrandclubProduct}
          allowNonEligibleClick={canViewAlternativeOffers}
          isEligibleForCheckout={isEligibleForCheckout}
          hideRewards={hideRewards}
          nonEligibleButtonText={canViewAlternativeOffers ? 'View' : undefined}
          subscription={buttonSubscription}
        />
        {primeTargetId && amazonSku ? (
          <div className="bc-prime-delivery-card" style={{ marginTop: 12 }}>
            {/* A new key mounts a new card for each variant, so Amazon renders the new SKU. */}
            <AmazonDeliveryCard
              key={amazonSku}
              targetId={primeTargetId}
              amazonSku={amazonSku}
              fontFamily={pageFontFamily()}
            />
          </div>
        ) : null}
      </>
    );

  const renderLocationMap = () =>
    enableLocationMap ? (
      <div style={{ marginTop: '30px' }} className="bc-location-map-container">
        <Suspense fallback={null}>
          <StoreLocator
            mobile={mobile}
            stacklineSku={currentBrandclubProduct?.stacklineSku}
            API_URL={HOST_DOMAIN}
            product={currentBrandclubProduct}
            onAddProductToBag={() => {
              currentBrandclubProduct?.matchedProducts?.length
                ? BuyBoxDialog.show({
                    checkoutAllowedOfferType: domainConfig?.checkoutAllowedOfferType,
                    products: [
                      {
                        ...currentBrandclubProduct,
                      },
                      ...currentBrandclubProduct?.matchedProducts,
                    ],
                    onClick: handleBuyClick,
                    onProductPageClick: (product) => {
                      trackCheckoutBarAction(AnalyticsTrackingEvent.WEB_APP_ORGANIC_OPEN_RETAILER_PRODUCT_PAGE, {
                        stacklineSku: product.stacklineSku,
                        retailerId: product.retailerId,
                        retailerSku: product.retailerSku,
                        retailPrice: product.retailPrice,
                        organicType: 'openRetailerProductPage',
                        source: 'brandclub_dtc_location_map',
                      });
                      window.open(product.retailerProductPageUrl, '_blank');
                    },
                  })
                : handleBuyClick(priorityProduct as ProductRewardSearchResult);
            }}
          />
        </Suspense>
      </div>
    ) : null;

  return (
    <>
      <CatalogV2InpageAddToBagContent
        renderAddToBagButton={renderAddToBagButton}
        renderLocationMap={renderLocationMap}
        renderRetailerInfo={() => null}
      />
      {/* // if the buy box is open, we don't want to show the scroll slider */}
      {enableScrollSliderDisplay && !isBuyBoxOpen && currentBrandclubProduct && !hideAddToBagCta ? (
        <ScrollSliderContainer className={isScrollSliderOn ? 'scroll-on' : 'scroll-off'}>
          <div className="scroll-slider-inner">
            <AddToBagButton
              mobile={mobile}
              onAddToCart={addPriorityProduct}
              onViewRetailer={handleNonEligibleClick}
              currentBrandclubProduct={priorityProduct as ProductRewardSearchResult}
              originalProduct={currentBrandclubProduct}
              allowNonEligibleClick={canViewAlternativeOffers}
              isEligibleForCheckout={isEligibleForCheckout}
              hideRewards
              nonEligibleButtonText={canViewAlternativeOffers ? 'View' : undefined}
              subscription={buttonSubscription}
            />
          </div>
        </ScrollSliderContainer>
      ) : null}
    </>
  );
};

export default CatalogV2InpageAddToBagButton;
